import * as R from 'ramda';
import shippingApi from '../shippingApi';

// example mock
/*
queryFn: ({ params }) =>
  import('./mockData/viewTrailerDetailsByLocation').then(({ default: getTrailers }) => ({
    data: getTrailers(params),
  })),
*/

const getUrl = R.concat('batches/');

const batchesEndpoints = shippingApi.injectEndpoints({
  endpoints: ({ mutation, query }) => ({
    acceptDistribution: mutation({
      invalidatesTags: ['Distributions'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('accept-distribution'),
      }),
    }),
    acceptShipment: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('accept-shipment'),
      }),
    }),
    bulkAcceptShipment: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('bulk-accept-shipments'),
      }),
    }),
    cancelBatch: mutation({
      invalidatesTags: ['Batches', 'Distributions', 'Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('cancel-batch'),
      }),
    }),
    cancelShipment: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('cancel-shipment'),
      }),
    }),
    createBatch: mutation({
      invalidatesTags: ['Batches'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('create-batch'),
      }),
    }),
    createDistributions: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('create-distributions'),
      }),
    }),
    createShipments: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('create-shipments'),
      }),
    }),
    deleteDistribution: mutation({
      invalidatesTags: ['Distributions', 'Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('delete-distribution'),
      }),
    }),
    editBatch: mutation({
      invalidatesTags: (result, error, { batchId }) => [{ id: batchId, type: 'Batches' }],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('edit-batch'),
      }),
    }),
    editDistribution: query({
      providesTags: ['Distributions'],
      query: ({ params }) => ({
        params,
        url: getUrl('edit-distribution'),
      }),
    }),
    editDistributions: mutation({
      invalidatesTags: ['Distributions', 'Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('edit-distribution'),
      }),
    }),
    editShipment: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('edit-shipment'),
      }),
    }),
    get1090ProductsDesignations: query({
      providesTags: ['Products'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-1090-product-designations'),
      }),
    }),
    getBaseProducts: query({
      // TODO: handle providesTags better
      providesTags: ['Products'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-base-products'),
      }),
    }),
    getBatchShipments: query({
      providesTags: ['Batches', 'Shipments'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-batch-shipments'),
      }),
    }),
    getBatches: query({
      providesTags: ['Batches'],
      query: ({ params }) => ({
        keepUnusedDataFor: 10,
        params,
        url: getUrl('get-batches'),
      }),
    }),
    getBatchesFilters: query({
      providesTags: ['Filters'],
      query: ({ params }) => ({
        keepUnusedDataFor: 10,
        params,
        url: getUrl('get-batches-filter-values'),
      }),
    }),

    getBookingOrigins: query({
      // TODO: handle providesTags better
      providesTags: ['BookingOrigins'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-booking-origins'),
      }),
    }),
    getDistributions: query({
      providesTags: ['Distributions'],
      query: ({ params }) => ({
        keepUnusedDataFor: 10,
        params,
        url: getUrl('get-distributions'),
      }),
    }),
    getDistributionsFilters: query({
      providesTags: ['Filters'],
      query: ({ params }) => ({
        keepUnusedDataFor: 10,
        params,
        url: getUrl('get-distributions-filter-values'),
      }),
    }),
    getEditBatch: query({
      invalidatesTags: (result, error, { batchId }) => [{ id: batchId, type: 'Batches' }],
      query: ({ params }) => ({
        params,
        url: getUrl('edit-batch'),
      }),
    }),
    getEditShipment: query({
      providesTags: ['Shipments'],
      query: ({ params }) => ({
        params,
        url: getUrl('edit-shipment'),
      }),
    }),
    getInventoryOwners: query({
      providesTags: ['InventoryOwners'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-inventory-owners'),
      }),
    }),
    getLocationInfo: query({
      // TODO: handle providesTags better
      providesTags: ['Locations'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-locations'),
      }),
    }),
    getLocations: query({
      // TODO: handle providesTags better
      providesTags: ['Locations'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-locations'),
      }),
    }),
    getOrigins: query({
      // TODO: handle providesTags better
      providesTags: ['Origins'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-origins'),
      }),
    }),
    getRecentBatches: query({
      providesTags: result =>
        result && result.length
          ? [...result.map(({ batchId }) => ({ id: batchId, type: 'Batches' }))]
          : ['Batches'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-recent-batches'),
      }),
    }),
    getRecipients: query({
      providesTags: ['Distributions'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-recipients'),
      }),
    }),
    getRestrictedLocations: query({
      providesTags: ['Locations'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-restricted-locations'),
      }),
    }),
    getShipmentDistributions: query({
      providesTags: ['Distributions', 'Shipments'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-shipment-distributions'),
      }),
      transformResponse: res => {
        res.distributions = res.distributions.map(({ chaining, chainRecipients, ...rest }) => ({
          ...rest,
          chainRecipients: chainRecipients.map(r => ({
            recipientIdName: r.chainContactAffiliate.companyCustomerIdName,
            recipientName: r.inventoryOwnerName,
          })),
          shipmentChain: chaining,
        }));

        return res;
      },
    }),
    getShipments: query({
      providesTags: ['Shipments'],
      query: ({ params }) => ({
        keepUnusedDataFor: 10,
        params,
        url: getUrl('get-shipments'),
      }),
    }),
    getShipmentsFilters: query({
      providesTags: ['Filters'],
      query: ({ params }) => ({
        keepUnusedDataFor: 10,
        params,
        url: getUrl('get-shipments-filter-values'),
      }),
    }),
    getShippers: query({
      // TODO: handle providesTags better
      providesTags: ['Shippers'],
      query: ({ params }) => ({
        params,
        url: getUrl('get-shippers'),
      }),
    }),
    rejectDistribution: mutation({
      invalidatesTags: ['Distributions'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('reject-distribution'),
      }),
    }),
    rejectShipment: mutation({
      invalidatesTags: ['Shipments'],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('reject-shipment'),
      }),
    }),
    saveDistribution: mutation({
      invalidatesTags: (result, error, { shipmentId }) => [
        { id: shipmentId, type: 'Shipment' },
        { id: 'LIST', type: 'Distribution' },
        'Distributions',
      ],
      query: ({ body }) => ({
        body,
        method: 'POST',
        url: getUrl('edit-distribution'),
      }),
    }),
    viewBatch: query({
      providesTags: ['Batches'],
      query: ({ params }) => ({
        params,
        url: getUrl('view-batch'),
      }),
    }),
    viewDistribution: query({
      providesTags: ['Distributions'],
      query: ({ params }) => ({
        params,
        url: getUrl('view-distribution'),
      }),
    }),
    viewShipment: query({
      providesTags: (result, _, { shipmentId }) => [{ id: shipmentId, type: 'Shipment' }],
      query: ({ params }) => ({
        params,
        url: getUrl('view-shipment'),
      }),
    }),
  }),
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useAcceptDistributionMutation,
  useAcceptShipmentMutation,
  useBulkAcceptShipmentMutation,
  useCancelBatchMutation,
  useDeleteDistributionMutation,
  useCancelShipmentMutation,
  useCreateBatchMutation,
  useCreateDistributionsMutation,
  useCreateShipmentsMutation,
  useEditBatchMutation,
  useEditDistributionsMutation,
  useEditDistributionQuery,
  useEditShipmentMutation,
  useGetBaseProductsQuery,
  useGetBatchOwnersQuery,
  useGet1090ProductsDesignationsQuery,
  useGetBatchShipmentsQuery,
  useGetBatchesQuery,
  useGetRecentBatchesQuery,
  useGetBatchesFiltersQuery,
  useGetBookingOriginsQuery,
  useGetDistributionsQuery,
  useGetDistributionsFiltersQuery,
  useGetEditBatchQuery,
  useGetEditShipmentQuery,
  useGetInventoryOwnersQuery,
  useLazyGetLocationInfoQuery,
  useGetLocationsQuery,
  useGetOriginsQuery,
  useGetRecipientsQuery,
  useGetRestrictedLocationsQuery,
  useGetShipmentDistributionsQuery,
  useGetShipmentsQuery,
  useGetShipmentsFiltersQuery,
  useGetShippersQuery,
  useRejectDistributionMutation,
  useRejectShipmentMutation,
  useSaveDistributionMutation,
  useViewBatchQuery,
  useViewDistributionQuery,
  useViewShipmentQuery,
} = batchesEndpoints;
